import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { geo as geoUtil, type Geo } from '@kilat/shared';
import { GeoMap, pageStyle, type GeoMarker, type GeoMarkerTone } from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { outletDocRepoFor } from '../lib/repositories/outlet-repos.js';
import { logger } from '../lib/logger.js';

/**
 * /map — the operator's own outlets on one Leaflet/OSM map (feature:
 * map-dashboards). A pure READ surface: one-shot `list()` of
 * `tenants/{tid}/outlets` (outlet docs are public-read; no rules change), split
 * into placed pins vs. outlets with no saved location. No money / auth / write
 * paths are touched.
 *
 * No-dead-ends: sign-in, no-tenant, loading, error, empty, and
 * outlets-without-a-location each land in an explicit screen with a next action.
 * The shared <GeoMap> primitive (packages/ui) is the same map find + the agent
 * PWA + the super-admin network map use.
 */

/**
 * Loose read shape of a `tenants/{tid}/outlets/{oid}` doc — the repo types
 * `profile` as `unknown`, so we narrow to the handful of fields the map needs,
 * exactly as `use-active-outlet.tsx` already does for the switcher.
 */
interface OutletDocShape {
  readonly id?: string;
  readonly profile?: {
    readonly name?: string;
    readonly address?: string;
    readonly status?: string;
    readonly geo?: { readonly lat?: number; readonly lng?: number } | null;
  };
}

interface MapOutlet {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly status: string;
  readonly geo: Geo | null;
}

type LoadState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly outlets: readonly MapOutlet[] }
  | { readonly phase: 'error' };

function readOutlet(doc: OutletDocShape): MapOutlet {
  const p = doc.profile ?? {};
  const g = p.geo;
  const parsedGeo: Geo | null =
    g && typeof g.lat === 'number' && typeof g.lng === 'number' ? { lat: g.lat, lng: g.lng } : null;
  return {
    id: doc.id ?? '',
    name: typeof p.name === 'string' && p.name.length > 0 ? p.name : (doc.id ?? 'Outlet'),
    address: typeof p.address === 'string' ? p.address : '',
    status: typeof p.status === 'string' ? p.status : 'active',
    geo: parsedGeo,
  };
}

const KNOWN_STATUS = new Set(['active', 'provisioned', 'soft_closed', 'archived']);

function statusKey(status: string): string {
  return KNOWN_STATUS.has(status) ? status : 'active';
}

function outletTone(status: string): GeoMarkerTone {
  switch (status) {
    case 'active':
      return 'positive';
    case 'provisioned':
      return 'primary';
    case 'soft_closed':
      return 'warning';
    case 'archived':
      return 'neutral';
    default:
      return 'primary';
  }
}

export function MapPage() {
  const { claims, user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setOutletId } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    outletDocRepoFor(tenantId)
      .list()
      .then((docs) => {
        if (cancelled) return;
        setState({ phase: 'ready', outlets: docs.map((d) => readOutlet(d as OutletDocShape)) });
      })
      .catch((e: unknown) => {
        logger.error('map: outlet load failed', { error: String(e) });
        if (!cancelled) setState({ phase: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, attempt]);

  const openQueue = useCallback(
    (outletId: string) => {
      setOutletId(outletId);
      navigate('/queue');
    },
    [setOutletId, navigate],
  );

  const { placed, unplaced } = useMemo(() => {
    if (state.phase !== 'ready') return { placed: [] as MapOutlet[], unplaced: [] as MapOutlet[] };
    const parts = geoUtil.partitionByLocation(state.outlets, (o) => o.geo);
    return { placed: [...parts.placed], unplaced: [...parts.unplaced] };
  }, [state]);

  const markers = useMemo<GeoMarker[]>(
    () =>
      placed
        .filter((o): o is MapOutlet & { geo: Geo } => o.geo !== null)
        .map((o) => ({
          id: o.id,
          lat: o.geo.lat,
          lng: o.geo.lng,
          label: o.name,
          tone: outletTone(o.status),
          popup: (
            <OutletPopup
              name={o.name}
              address={o.address}
              statusLabel={t(`dashboard.map.status.${statusKey(o.status)}`)}
              onOpenQueue={() => openQueue(o.id)}
              viewQueueLabel={t('dashboard.map.viewQueue')}
              addressMissing={t('dashboard.map.addressMissing')}
            />
          ),
        })),
    [placed, t, openQueue],
  );

  return (
    <main style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-h1)' }}>
            {t('dashboard.map.title')}
          </h1>
          <p style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
            {t('dashboard.map.subtitle')}
          </p>
        </div>
      </header>

      {authLoading || (tenantId && state.phase === 'loading') ? (
        <StatusLine text={t('dashboard.map.loading')} />
      ) : !user ? (
        <Notice title={t('dashboard.map.signIn')} />
      ) : !tenantId ? (
        <Notice title={t('dashboard.map.noTenant')} />
      ) : state.phase === 'error' ? (
        <Notice
          tone="error"
          title={t('dashboard.map.errorTitle')}
          body={t('dashboard.map.errorBody')}
          action={{ label: t('dashboard.map.retry'), onClick: () => setAttempt((a) => a + 1) }}
        />
      ) : state.phase === 'ready' && state.outlets.length === 0 ? (
        <Notice
          title={t('dashboard.map.empty')}
          action={{ label: t('dashboard.map.emptyCta'), onClick: () => navigate('/outlets') }}
        />
      ) : (
        <>
          {markers.length > 0 ? (
            <GeoMap markers={markers} ariaLabel={t('dashboard.map.mapAria')} height={460} />
          ) : (
            <Notice
              title={t('dashboard.map.unmappedTitle', { count: unplaced.length })}
              body={t('dashboard.map.unmappedBody')}
              action={{
                label: t('dashboard.map.manageOutlets'),
                onClick: () => navigate('/outlets'),
              }}
            />
          )}

          {markers.length > 0 && unplaced.length > 0 ? (
            <section
              style={unmappedCardStyle}
              aria-label={t('dashboard.map.unmappedTitle', { count: unplaced.length })}
            >
              <h2 style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-h3)' }}>
                {t('dashboard.map.unmappedTitle', { count: unplaced.length })}
              </h2>
              <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-on-surface-muted)' }}>
                {t('dashboard.map.unmappedBody')}
              </p>
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                {unplaced.map((o) => (
                  <li key={o.id} style={unmappedRowStyle}>
                    <span style={{ fontWeight: 600 }}>{o.name}</span>
                    <span
                      style={{
                        color: 'var(--color-on-surface-muted)',
                        fontSize: 'var(--text-meta)',
                      }}
                    >
                      {o.address || t('dashboard.map.addressMissing')}
                    </span>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => navigate('/outlets')} style={secondaryBtnStyle}>
                {t('dashboard.map.manageOutlets')}
              </button>
            </section>
          ) : null}
        </>
      )}
    </main>
  );
}

function OutletPopup({
  name,
  address,
  statusLabel,
  onOpenQueue,
  viewQueueLabel,
  addressMissing,
}: {
  readonly name: string;
  readonly address: string;
  readonly statusLabel: string;
  readonly onOpenQueue: () => void;
  readonly viewQueueLabel: string;
  readonly addressMissing: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 160 }}>
      <strong style={{ fontSize: 'var(--text-body)' }}>{name}</strong>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {statusLabel}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {address || addressMissing}
      </span>
      <button type="button" onClick={onOpenQueue} style={popupBtnStyle}>
        {viewQueueLabel}
      </button>
    </div>
  );
}

function StatusLine({ text }: { readonly text: string }) {
  return (
    <p role="status" aria-live="polite" style={{ color: 'var(--color-on-surface-muted)' }}>
      {text}
    </p>
  );
}

function Notice({
  title,
  body,
  tone,
  action,
}: {
  readonly title: string;
  readonly body?: string;
  readonly tone?: 'error';
  readonly action?: { readonly label: string; readonly onClick: () => void };
}) {
  return (
    <div role={tone === 'error' ? 'alert' : undefined} style={{ maxWidth: 520 }}>
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--text-h2)',
          color: tone === 'error' ? 'var(--color-tone-error, #b91c1c)' : undefined,
        }}
      >
        {title}
      </h2>
      {body ? (
        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-on-surface-muted)' }}>
          {body}
        </p>
      ) : null}
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          style={{ ...primaryBtnStyle, marginTop: 'var(--space-4)' }}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: 'var(--space-4)',
  flexWrap: 'wrap',
};

const unmappedCardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  padding: 'var(--space-4) var(--space-5)',
};

const unmappedRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
  borderBottom: '1px solid var(--color-border)',
  flexWrap: 'wrap',
};

const primaryBtnStyle: CSSProperties = {
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-5)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
};

const secondaryBtnStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-4)',
  border: '1px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

const popupBtnStyle: CSSProperties = {
  marginTop: 2,
  minHeight: 36,
  padding: '0 var(--space-3)',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
